import React, { Component } from 'react';
import { Alert } from 'reactstrap';
import moment from 'moment';
import { WorkOrderForm } from './WorkOrderForm';
import { LoadingModal } from './LoadingModal';
import { FetchData, UploadFile } from './utils/DataFetch';
import { GetCookie, GetParameterByName } from './utils/Common';
import { NewWorkOrderModel } from './utils/NewWorkOrderModel';
import { DecodeLinkPayload, CaseInsensitive } from './utils/LinkPayload';

const MAX_HISTORY_ENTRIES = 20;
const MAX_FILE_BYTES = 25 * 1024 * 1024;
const FILE_DOWNLOAD_TIMEOUT_MS = 30000;

// A link is only as trusted as whoever wrote it, so attachments come only from the hosts configured in
// REACT_APP_LINK_FILE_HOSTS (comma-separated, e.g. OfficeAdminPro's file storage). Local development
// also allows localhost, where local Supabase serves files.
const LINK_FILE_HOSTS = (process.env.REACT_APP_LINK_FILE_HOSTS || '')
  .split(',')
  .map((host) => host.trim().toLowerCase())
  .filter(Boolean);
const DEV_FILE_HOSTS = process.env.NODE_ENV === 'development' ? ['localhost', '127.0.0.1'] : [];

function fileUrlProblem(raw) {
  let url;
  try {
    url = new URL(raw);
  } catch {
    return 'not a valid address';
  }
  const host = url.hostname.toLowerCase();
  if (DEV_FILE_HOSTS.includes(host)) return null;
  if (url.protocol !== 'https:') return 'not https';
  return LINK_FILE_HOSTS.includes(host) ? null : `${host} is not an allowed file host`;
}

async function downloadLinkFile(fileUrl) {
  const problem = fileUrlProblem(fileUrl);
  if (problem) throw new Error(problem);

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FILE_DOWNLOAD_TIMEOUT_MS);
  try {
    const res = await fetch(fileUrl, { signal: controller.signal });
    if (!res.ok) throw new Error(`file ${res.status}`);
    if (Number(res.headers.get('content-length')) > MAX_FILE_BYTES) throw new Error('larger than 25 MB');
    const blob = await res.blob();
    if (blob.size > MAX_FILE_BYTES) throw new Error('larger than 25 MB');
    return blob;
  } catch (err) {
    throw err.name === 'AbortError' ? new Error('took longer than 30 seconds') : err;
  } finally {
    clearTimeout(timer);
  }
}

// The work-order agent's email links here with the request body in ?job=.
async function fetchFirst(url, token, filter) {
  const data = await FetchData(url, 'POST', token, filter);
  return Array.isArray(data) && data.length > 0 ? data[0] : null;
}

/**
 * The link's optional "history" list: entries written to the new job's history once it's saved. An
 * entry with a FileUrl (e.g. the work-order agent's PDF of the original request email, served by
 * OfficeAdminPro) has that file downloaded and attached to its history entry.
 */
function parseHistory(raw) {
  return (Array.isArray(raw) ? raw : [])
    .map((entry) => {
      const field = CaseInsensitive(entry);
      return {
        JobHistoryType: field('JobHistoryType') || (field('FileUrl') ? 'Attachment' : 'Job Note'),
        JobHistoryName: field('JobHistoryName') || '',
        Description: field('Description') || '',
        Contact: field('Contact') || '',
        ContactEmail: field('ContactEmail') || '',
        FileUrl: field('FileUrl') || '',
        FileName: field('FileName') || '',
      };
    })
    .filter((entry) => entry.Description.trim() !== '' || entry.FileUrl !== '')
    .slice(0, MAX_HISTORY_ENTRIES);
}

export class WorkOrderApi extends Component {
  static displayName = WorkOrderApi.name;

  constructor(props) {
    super(props);
    this.user = JSON.parse(GetCookie('user'));
    this.history = [];
    this.historySaving = null;
    this.state = { model: null, modal: false, fetchingData: false, alert: null, jobHistoryList: [] };
  }

  componentDidMount() {
    const job = GetParameterByName('job');
    if (!job) {
      this.setState({ alert: { color: 'warning', message: 'This link has no work order data.' } });
      return;
    }

    let request;
    try {
      request = DecodeLinkPayload(job);
    } catch (err) {
      this.setState({ alert: { color: 'danger', message: 'The link from the email is damaged. Ask for a new draft.' } });
      return;
    }
    this.openForm(request);
  }

  async openForm(request) {
    const job = CaseInsensitive(CaseInsensitive(request)('job'));
    this.history = parseHistory(CaseInsensitive(request)('history'));
    this.setState({ fetchingData: true, alert: null });

    const model = NewWorkOrderModel();
    const jobDateTime = job('JobDateTime') ? moment(job('JobDateTime')) : null;
    Object.assign(model, {
      ClmId: job('ClmId') || '',
      ClientId: job('ClientId') || '',
      RepId: job('RepId') || null,
      IntId: job('IntId') || null,
      AppointmentTypeId: job('AppointmentTypeId') || model.AppointmentTypeId,
      RemType: job('RemTypeId') || null,
      RemHost: job('RemHostId') || null,
      JobDateTime: jobDateTime && jobDateTime.isValid() ? jobDateTime.format('YYYY-MM-DDTHH:mm') : null,
      JobTime: jobDateTime && jobDateTime.isValid() ? jobDateTime.format('h:mm A') : null,
      WorkFlowId: job('WorkFlowId') || '',
      CallerType: job('CallerType') ?? null,
      CallerNote: job('CallerNote') || '',
      ProvId: job('ProvId') || null,
      ProviderName: job('ProviderName') || '',
      AptName: job('AptName') || '',
      AptStreet: job('AptStreet') || '',
      AptCity: job('AptCity') || '',
      AptState: job('AptState') || '',
      AptZip: job('AptZip') || '',
      AptPhone: job('AptPhone') || '',
      IntApptNotes: job('IntApptNotes') || '',
      RemIntFlg: job('RemIntFlg') === true,
      RemClaimFlg: job('RemClaimFlg') === true,
      RemProvFlg: job('RemProvFlg') === true,
      RemFinalFlg: job('RemFinalFlg') === true,
    });

    // A claimant who wasn't in ELA when the email was sent has no ClmId in the link, only who they are.
    // The Scheduler creates them with the email's Create Claimant link first; find them now.
    const hint = request && CaseInsensitive(request)('claimant');
    if (!model.ClmId && hint) {
      const want = CaseInsensitive(hint);
      const noDash = (s) => (s || '').replace(/[^0-9A-Za-z]/g, '').toLowerCase();
      const lower = (s) => (s || '').trim().toLowerCase();
      try {
        const found = await FetchData('/getclaimants', 'POST', this.user.token, {
          ClientId: model.ClientId || '',
          ClmClaimNumberNoDash: want('ClmClaimNumber') ? noDash(want('ClmClaimNumber')) : '',
          LastName: want('ClmClaimNumber') ? '' : want('ClmLastName') || '',
          FirstName: want('ClmClaimNumber') ? '' : want('ClmFirstName') || '',
        });
        const matches = (Array.isArray(found) ? found : []).filter((c) =>
          want('ClmClaimNumber')
            ? noDash(c.clmClaimNumber) === noDash(want('ClmClaimNumber'))
            : lower(c.clmFirstName) === lower(want('ClmFirstName')) && lower(c.clmLastName) === lower(want('ClmLastName')),
        );
        if (matches.length === 1) model.ClmId = matches[0].clmId;
      } catch (err) {
        // Falls through to the "create the claimant first" message below.
      }
      if (!model.ClmId) {
        const who = [want('ClmFirstName'), want('ClmLastName')].filter(Boolean).join(' ');
        this.setState({
          fetchingData: false,
          alert: { color: 'warning', message: `${who || 'The claimant'} isn't in ELA yet. Create the claimant with the email's first button, then click Create Job again.` },
        });
        return;
      }
    }

    try {
      const token = this.user.token;
      const [rep, claimant, interpreter] = await Promise.all([
        model.RepId ? fetchFirst('/getreps', token, { RepId: model.RepId }) : null,
        model.ClmId ? fetchFirst('/getclaimants', token, { ClmId: model.ClmId }) : null,
        model.IntId ? fetchFirst('/getinterpreters', token, { IntId: model.IntId }) : null,
      ]);

      if (rep) {
        Object.assign(model, {
          RepFirstName: rep.repFirstName,
          RepLastName: rep.repLastName,
          RepEmail: rep.repEmail,
          RepPhone: rep.repPhone,
          ClientId: rep.clientId,
          ClientAbbrv: rep.clientAbbrv,
          ClientName: rep.clientName,
          ClientEmail: rep.clientEmail,
          ClientPhone: rep.clientPhone,
        });
      }
      if (claimant) {
        Object.assign(model, {
          ClmFirstName: claimant.clmFirstName,
          ClmLastName: claimant.clmLastName,
          ClmClaimNumber: claimant.clmClaimNumber,
          ClmClaimNumberNoDash: claimant.clmClaimNumberNoDash,
          ClmPhone: claimant.clmPhone,
          ClmDateOfLoss: claimant.clmDateOfLoss,
          ClmDateOfLossDate: claimant.clmDateOfLossDate,
          ClmDateOfInjury: claimant.clmDateOfInjury,
          ClmDateOfInjuryStart: claimant.clmDateOfInjuryStart,
          ClmDateOfInjuryEnd: claimant.clmDateOfInjuryEnd,
          ClmNote: claimant.clmNote,
          ClmDOB: claimant.clmDOB,
          ClmInsuredParty: claimant.clmInsuredParty,
          ClmEmployer: claimant.clmEmployer,
          ClmInvoiceCaption: claimant.clmInvoiceCaption,
          ClmLanguage: claimant.clmLanguage,
          ClmWCABNumber: claimant.clmWCABNumber,
        });
      }
      if (interpreter) {
        Object.assign(model, {
          IntLastName: interpreter.intLastName,
          IntFirstName: interpreter.intFirstName,
          IntCellTxt: interpreter.intCellTxt,
          IntPhoneOnly: interpreter.intPhoneOnly,
          IntEmail: interpreter.intEmail,
          IntNote: interpreter.intNote,
          InHouseFlg: interpreter.inHouseFlg,
          IntCity: interpreter.intCity,
          IntStreet: interpreter.intStreet,
          IntState: interpreter.intState,
          IntZip: interpreter.intZip,
          Rate: interpreter.rate,
          HourMinimum: interpreter.hourMinimum,
          Prorate: interpreter.prorate,
        });
      }

      const missing = [
        model.RepId && !rep && 'rep',
        model.ClmId && !claimant && 'claimant',
        model.IntId && !interpreter && 'interpreter',
      ].filter(Boolean);

      this.setState({
        model,
        modal: true,
        alert: missing.length
          ? { color: 'warning', message: `Could not find the ${missing.join(', ')} from the email. Pick them in the form.`, isOpen: true }
          : null,
      });
    } catch (err) {
      this.setState({ alert: { color: 'danger', message: 'Could not load the details for this work order.' } });
    } finally {
      this.setState({ fetchingData: false });
    }
  }

  // Closing the form, or saving and closing it, lands the user on the work order list where the new job now shows.
  setModal = () => {
    this.goToWorkOrderList();
  };

  // Waits for the link's history entries so leaving right after Save doesn't drop them; a failure keeps the user here to see it.
  async goToWorkOrderList() {
    if (this.historySaving && !(await this.historySaving)) {
      this.setState({ modal: false });
      return;
    }
    const jobDate = this.state.model?.JobDateTime ? moment(this.state.model.JobDateTime) : null;
    window.location.href = jobDate && jobDate.isValid() ? `/?date=${jobDate.format('YYYY-MM-DD')}` : '/';
  }

  setFetchingData = (fetchingData) => {
    this.setState({ fetchingData });
  };

  // The form reports the saved job here; the first time it has a JobId, the link's history entries are written to it.
  handleModelChange = (model) => {
    this.setState({ model });
    if (!this.historySaving && this.history.length > 0 && model.EventType === 'update' && model.JobId) {
      this.historySaving = this.saveHistory(model.JobId);
    }
  };

  /** Writes each history entry through /savejobhistory as the logged-in user. Resolves true when all saved. */
  async saveHistory(jobId) {
    const failures = [];
    for (const entry of this.history) {
      const formData = new FormData();
      formData.append('EventType', 'insert');
      formData.append('JobId', jobId);
      formData.append('JobHistoryType', entry.JobHistoryType);
      formData.append('JobHistoryName', entry.JobHistoryName);
      formData.append('Description', entry.Description);
      formData.append('Contact', entry.Contact);
      formData.append('ContactEmail', entry.ContactEmail);
      formData.append('CreateByUserId', this.user.userid);

      try {
        if (entry.FileUrl) {
          // Same fields as the history screen's own upload (WorkOrderAddJobNotes.js).
          const blob = await downloadLinkFile(entry.FileUrl);
          const fileName = entry.FileName || 'attachment.pdf';
          formData.append('FileLocation', '');
          formData.append('FileName', fileName);
          formData.append('FormFile', blob, fileName);
        }
      } catch (err) {
        if (!entry.Description.trim()) {
          failures.push(`Couldn't download "${entry.FileName || entry.FileUrl}" (${err.message})`);
          continue;
        }
        failures.push(`Couldn't download "${entry.FileName || entry.FileUrl}" (${err.message}); the note was saved without it`);
      }

      try {
        const result = await UploadFile('/savejobhistory', this.user.token, formData);
        if (result !== 'Success') failures.push(result || 'Unknown error');
      } catch (err) {
        failures.push('Could not reach the server');
      }
    }

    const jobHistoryList = await FetchData('/getjobhistory', 'POST', this.user.token, { JobId: jobId });
    this.setState({
      jobHistoryList: Array.isArray(jobHistoryList) ? jobHistoryList : [],
      alert: failures.length
        ? {
            color: 'danger',
            message: `The work order saved, but ${failures.length} of ${this.history.length} history entries did not: ${failures.join('; ')}`,
            isOpen: true,
          }
        : this.state.alert,
    });
    return failures.length === 0;
  }

  handleSaveEventSuccess = (alert) => {
    if (!alert.modal) {
      this.goToWorkOrderList();
      return;
    }
    this.setState({ alert: { color: alert.color, message: alert.message, isOpen: true } });
  };

  render() {
    const { model, modal, fetchingData, alert, jobHistoryList } = this.state;

    return (
      <div>
        <LoadingModal fetchingData={fetchingData} />
        {alert && !modal && <Alert color={alert.color}>{alert.message}</Alert>}
        {modal && (
          <WorkOrderForm
            model={model}
            modal={modal}
            alert={alert || { color: 'success', message: '', isOpen: false }}
            setModal={this.setModal}
            onModelChange={this.handleModelChange}
            refreshWorkOrderData={() => {}}
            workOrderList={[]}
            jobHistoryList={jobHistoryList}
            jobInvLineList={[]}
            setNextPrevData={() => {}}
            selectedIndex={-1}
            handleSaveEventSuccess={this.handleSaveEventSuccess}
            tableView="default"
            setFetchingData={this.setFetchingData}
            viewInvoice={() => {}}
          />
        )}
      </div>
    );
  }
}
